import { useAppStateQuery } from '@/hooks/useAppStateQuery'
import { STATE_KEY } from '@/lib/constants'
import { StateKey, StateValue } from '@/types/misc'

export function useAppState() {
  const appStateQuery = useAppStateQuery()
  const states = appStateQuery.data?.['hydra:member'] || []

  return {
    hasInteractedWithFirstRelationshipRequestApprovedModal: hasStateValue(
      STATE_KEY.hasInteractedWithFirstRelationshipRequestApprovedModal,
      true,
    ),

    hasNotInteractedWithFirstRelationshipRequestApprovedModal: hasStateValue(
      STATE_KEY.hasInteractedWithFirstRelationshipRequestApprovedModal,
      false,
    ),

    hasWatchedDemoVideo: hasStateValue(STATE_KEY.hasWatchedDemoVideo, true),

    hasInteractedWithRqCertifiedAnnouncement: hasStateValue(
      STATE_KEY.hasInteractedWithRqCertifiedAnnouncement,
      true,
    ),

    hasInteractedWithWalkthroughBanner: hasStateValue(
      STATE_KEY.hasInteractedWithWalkthroughBanner,
      true,
    ),

    hasInteractedWithIntellifloIntegrationAutosaveNotice: hasStateValue(
      STATE_KEY.hasInteractedWithIntellifloIntegrationAutosaveNotice,
      true,
    ),

    hasInteractedWithPlannrIntegrationAutosaveNotice: hasStateValue(
      STATE_KEY.hasInteractedWithPlannrIntegrationAutosaveNotice,
      true,
    ),

    hasInteractedWithEmailAndCalendarConnectionPrompt: hasStateValue(
      STATE_KEY.hasInteractedWithEmailAndCalendarConnectionPrompt,
      true,
    ),

    hasClientReviewedPartners: hasStateValue(
      STATE_KEY.hasClientReviewedPartners,
      true,
    ),

    panelApprovedNew: normalizeAsArrayOfNumbers(STATE_KEY.panelApprovedNew),
    panelApprovedUpdated: normalizeAsArrayOfNumbers(
      STATE_KEY.panelApprovedUpdated,
    ),
    panelRequestedNew: normalizeAsArrayOfNumbers(STATE_KEY.panelRequestedNew),
    panelRequestedUpdated: normalizeAsArrayOfNumbers(
      STATE_KEY.panelRequestedUpdated,
    ),

    panelRequestPending: normalizeAsArrayOfNumbers(
      STATE_KEY.panelRequestPending,
    ),

    referralFromNew: normalizeAsArrayOfNumbers(STATE_KEY.referralFromNew),
    referralFromUpdated: normalizeAsArrayOfNumbers(
      STATE_KEY.referralFromUpdated,
    ),
    referralToNew: normalizeAsArrayOfNumbers(STATE_KEY.referralToNew),
    referralToUpdated: normalizeAsArrayOfNumbers(STATE_KEY.referralToUpdated),
    referralApprovalRequestNew: normalizeAsArrayOfNumbers(
      STATE_KEY.referralApprovalRequestNew,
    ),

    // The relationship onboarding state key only exists for companies seeded
    // by the TaxAssist bulk importer, so we distinguish "key present with
    // value false" (pending) from "key absent" (not applicable).
    relationshipOnboardingModalPending: hasStateValue(
      STATE_KEY.relationshipOnboardingModal,
      false,
    ),
    relationshipOnboardingModalComplete: hasStateValue(
      STATE_KEY.relationshipOnboardingModal,
      true,
    ),
  }

  function getByStateKey(stateKey: StateKey) {
    return states.find((state) => state.stateKey === stateKey)
  }

  function hasStateValue(stateKey: StateKey, stateValue: StateValue): boolean {
    return states.some(
      (state) => state.stateKey === stateKey && state.stateValue === stateValue,
    )
  }

  function normalizeAsArrayOfNumbers(stateKey: StateKey): number[] {
    const value = getByStateKey(stateKey)?.stateValue
    return Array.isArray(value) ? value.map(Number) : []
  }
}
